import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Activity, Pencil, Plus, Tag } from 'lucide-react';
import { PageHeader } from '@/shared/components/page-header';
import { EmptyState } from '@/shared/components/empty-state';
import { Card } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Switch } from '@/shared/ui/switch';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { ActionIconButton } from '@/shared/components/action-icon-button';
import {
  useLoyaltyCampaigns,
  setCampaignActive,
  BENEFIT_KINDS,
  type LoyaltyCampaign,
} from '../api/use-loyalty-admin';
import { channelLabel, paymentLabel } from '../model/campaign-scope';

/**
 * Lista de campanhas do motor de fidelidade.
 *
 * Passou a ser nosso quando o parceiro confirmou que o PDV não calcula
 * desconto nenhum: o motor devolve o payload já calculado e o caixa só
 * aplica. O que estiver errado aqui vira preço errado no cupom fiscal.
 */
export function LoyaltyCampaignsPage() {
  const { idPassbook } = useParams<{ idPassbook: string }>();
  const navigate = useNavigate();
  const base = `/loja/${idPassbook}/campanhas/fidelidade`;
  const { data = [], isLoading } = useLoyaltyCampaigns(idPassbook);
  const queryClient = useQueryClient();

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['loyalty-campaigns', idPassbook] });

  const toggle = async (c: LoyaltyCampaign) => {
    if (!idPassbook) return;
    try {
      await setCampaignActive(idPassbook, c.campaignId, !c.active);
      invalidate();
    } catch {
      toast.error('Falha ao atualizar a campanha.');
    }
  };

  return (
    <>
      <PageHeader
        title="Campanhas"
        description="O motor calcula o desconto e devolve pronto para o caixa. O que estiver errado aqui vira preço errado no cupom."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => navigate(`${base}/liquidacao`)}>
              <Activity className="size-4" /> Monitor de liquidação
            </Button>
            <Button onClick={() => navigate(`${base}/nova`)}>
              <Plus className="size-4" /> Nova campanha
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={Tag}
          title="Nenhuma campanha cadastrada"
          description="Enquanto não houver campanha, o orçamento responde sem desconto — e o caixa não aplica promoção nenhuma."
        />
      ) : (
        <div className="space-y-2">
          {data.map((c) => (
            <Card key={c._id} className="flex items-start justify-between gap-3 p-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs text-muted-foreground tabular-nums">
                    #{c.campaignId}
                  </span>
                  <span className={c.active ? 'font-medium' : 'font-medium text-muted-foreground line-through'}>
                    {c.name}
                  </span>
                  {c.idConflict > 0 ? (
                    <Badge variant="outline">conflito {c.idConflict}</Badge>
                  ) : null}
                  {c.cumulative ? <Badge variant="outline">cumulativa</Badge> : null}
                  {c.isGift ? <Badge variant="outline">brinde</Badge> : null}
                  {c.promocode ? <Badge variant="outline">código {c.promocode}</Badge> : null}
                  {c.requiresEnrollment ? <Badge variant="outline">exige ativar no app</Badge> : null}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">
                  {c.benefits
                    .map((b) => {
                      const kind = BENEFIT_KINDS.find((k) => k.value === b.kind)?.label ?? b.kind;
                      return `${b.productCode}: ${kind} ${b.value}`;
                    })
                    .join(' · ')}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Canais: {channelLabel(c.channels)} · Pagamento: {paymentLabel(c.paymentTypes)} · Lojas:{' '}
                  {c.storeCodes?.length ? c.storeCodes.join(', ') : 'todas'}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Switch checked={c.active} onCheckedChange={() => void toggle(c)} />
                <ActionIconButton
                  label="Editar campanha"
                  onClick={() => navigate(`${base}/${c.campaignId}/editar`)}
                >
                  <Pencil className="size-4" />
                </ActionIconButton>
              </div>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
